import Button from '@alltrails/shared/denali/components/Button';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import CustomProvider from 'components/CustomProvider';
import { Component, FormEvent, ReactNode, Suspense, useRef, useState } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import CurrencyCode from 'types/CurrencyCode';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { CardElementState, Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly';
import { RecurlyError, TokenPayload } from '@recurly/recurly-js';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { ApiError, put } from '@alltrails/shared/api';
import useFeatures from 'hooks/useFeatures';
import { NoSsr, Typography } from '@material-ui/core';
import hasValidationErrors from './utils/hasValidationErrors';
import CreditCardForm from '../CreditCardForm/CreditCardForm';
import * as styles from './styles/UpdatePaymentForm.module.scss';
import TokenizedCreditCardForm, { CARD_VALIDATION_NAMES } from '../TokenizedCreditCardForm';
import useTokenizedFormValidation from '../TokenizedCreditCardForm/hooks/useTokenizedFormValidation';

const error = <FormattedMessage defaultMessage="There was an error processing your payment. Please contact support." />;

type FormData = {
  billing_first_name?: string;
  billing_last_name?: string;
  cc_expiration_month?: number;
  cc_expiration_year?: number;
};

type Props = {
  updateBillingUrl?: string;
  formData?: FormData;
  currencyCode: CurrencyCode;
  languageRegionCode: LanguageRegionCode;
};

type TokenizeFormProps = {
  updateBillingUrl?: string;
  formData?: FormData;
};

type State = {
  errors: any;
  name: string;
  number: string;
  cvv: string;
  postalCode: string;
  serverError: any;
  submitting: boolean;
  expiration: string;
};

// This UpdatePaymentForm will likely be replaced by the TokenizedUpdatePaymentForm below (pending A/B test results).
export class UpdatePaymentForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { billing_first_name, billing_last_name, cc_expiration_month, cc_expiration_year } = props.formData;

    this.state = {
      errors: {
        name: '',
        number: '',
        cvv: '',
        postalCode: ''
      },
      name: `${billing_first_name} ${billing_last_name}`,
      number: '',
      cvv: '',
      postalCode: '',
      serverError: null,
      submitting: false,
      expiration: cc_expiration_month ? `${String(cc_expiration_month).padStart(2, '0')} / ${cc_expiration_year % 100}` : ''
    };
  }

  handleCreditCardFormChange = (event: any) => {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, this.state.errors);

    switch (name) {
      case 'cvv':
        this.setState({ cvv: value, errors: payload.errors });
        break;
      case 'expiration':
        this.setState({ expiration: value, errors: payload.errors });
        break;
      case 'name':
        this.setState({ name: value, errors: payload.errors });
        break;
      case 'number':
        this.setState({ number: value, errors: payload.errors });
        break;
      case 'postalCode':
        this.setState({ postalCode: value, errors: payload.errors });
        break;
      default:
        throw new Error(`Missing credit card form name ${name}`);
    }
  };

  handlePaymentEditSuccess = (data: any) => {
    window.location.href = LanguageSupportUtil.wrapUrlSafe(data.redirectUrl, this.props.languageRegionCode);
  };

  handlePaymentFormError = (err: any) => {
    let { errors, serverError } = this.state;

    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText).errors;
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        serverError = typeof errorObj.base[0] === 'string' ? errorObj.base[0] : error;
      }
      if (errorObj.cc_number) {
        errors.number = errorObj.cc_number[0];
      }

      if (errorObj.cc_expiration_month) {
        errors.expiration = errorObj.cc_expiration_month[0];
      }

      if (errorObj.cc_expiration_year) {
        errors.expiration = errorObj.cc_expiration_year[0];
      }

      if (errorObj.cc_csc) {
        errors.cvv = errorObj.cc_csc[0];
      }
    } else {
      serverError = error;
    }

    this.setState({ errors, serverError, submitting: false });
  };

  getSubmissionData() {
    const [cc_expiration_month, cc_expiration_year] = this.state.expiration.split(' / ');
    const nameArray = this.state.name.split(' ');
    const nameArrayClean = nameArray.filter((item: string) => item !== '');
    const firstName = nameArrayClean[0];
    const lastName = nameArrayClean[nameArrayClean.length - 1];

    return {
      user: {
        cc_number: this.state.number,
        cc_expiration_month,
        cc_expiration_year,
        cc_csc: this.state.cvv,
        zip: this.state.postalCode,
        first_name: firstName,
        last_name: lastName
      }
    };
  }

  failedFormValidation() {
    let validation;
    let validationErrors = { ...this.state.errors };

    validation = validatePaymentForm(this.state.errors.name, this.state.name, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    validation = validatePaymentForm(this.state.errors.number, this.state.number, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    validation = validatePaymentForm(this.state.errors.cvv, this.state.cvv, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    validation = validatePaymentForm(this.state.errors.postalCode, this.state.postalCode, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    this.setState({ errors: validationErrors });

    return hasValidationErrors(validationErrors);
  }

  submitCreditCardForm = (e: any) => {
    e.preventDefault();

    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });

    if (this.failedFormValidation()) {
      this.setState({ submitting: false });
      return;
    }

    this.setState({ errors: {} });

    const submissionData = this.getSubmissionData();

    ServerCommunicationUtil.putApiEndpoint(
      this.props.updateBillingUrl,
      submissionData,
      this.handlePaymentEditSuccess,
      this.handlePaymentFormError,
      () => {},
      { 'X-Language-Locale': this.props.languageRegionCode }
    );
  };

  renderCreditCardForm() {
    return (
      <form className={styles.form} id="update-payment-form" onSubmit={this.submitCreditCardForm}>
        <CreditCardForm
          changeHandler={this.handleCreditCardFormChange}
          cvv={this.state.cvv}
          errors={this.state.errors}
          expiration={this.state.expiration}
          isCouponInputShown={false}
          name={this.state.name}
          number={this.state.number}
          postalCode={this.state.postalCode}
          setError={(name, error) => this.setState({ errors: { ...this.state.errors, [name]: error } })}
        />
        {this.state.serverError && <div className={styles.serverError}>{this.state.serverError}</div>}
        <Button
          disabled={this.state.submitting}
          testId="update-payment-submit"
          text={<FormattedMessage defaultMessage="Save" />}
          type="submit"
          variant="primary"
          width="full"
        />
        <Button
          linkInfo={{ href: '/my/profile/edit' }}
          onClick={undefined}
          testId=""
          text={<FormattedMessage defaultMessage="Cancel" />}
          variant="flat"
        />
      </form>
    );
  }

  render() {
    return <div>{this.renderCreditCardForm()}</div>;
  }
}

const getInitialFullName = (formData: FormData) => {
  if (formData?.billing_first_name && formData?.billing_last_name) {
    return `${formData.billing_first_name} ${formData.billing_last_name}`;
  }
  return null;
};

export const TokenizedUpdatePaymentForm = ({ updateBillingUrl, formData }: TokenizeFormProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<ReactNode>(null);
  const reactRecurlyInstance = useRecurly();
  const formRef = useRef();
  const languageRegionCode = useLanguageRegionCode();
  const initialName = getInitialFullName(formData);
  const {
    validationErrors,
    setClientValidationErrors,
    setTokenRequestErrors,
    hasValidationErrors: hasTokenizedValidationErrors
  } = useTokenizedFormValidation(!!initialName);

  const defaultServerError = <FormattedMessage defaultMessage="Unable to update your credit card info." />;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (submitting || hasTokenizedValidationErrors()) {
      return;
    }

    setSubmitting(true);

    const onRecurlyToken = async (err: RecurlyError, token: TokenPayload) => {
      if (err) {
        setTokenRequestErrors(err);
        setSubmitting(false);
      } else {
        try {
          const { redirectUrl } = await put<{ redirectUrl: string }>(updateBillingUrl, { cc_token: token.id });
          window.location.assign(LanguageSupportUtil.wrapUrlSafe(redirectUrl, languageRegionCode));
        } catch (e) {
          if (e instanceof ApiError && e?.data?.errors?.base?.[0]) {
            setServerError(e.data.errors.base[0]);
          } else {
            setServerError(defaultServerError);
          }
          setSubmitting(false);
        }
      }
    };

    try {
      reactRecurlyInstance.token(formRef.current, onRecurlyToken);
    } catch (e) {
      setServerError(defaultServerError);
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} id="update-payment-form" onSubmit={handleSubmit} ref={formRef}>
      <TokenizedCreditCardForm
        initialFullName={initialName}
        validationErrors={validationErrors}
        handleInputChange={(
          input: CARD_VALIDATION_NAMES,
          value: string | { number: CardElementState; expiry: CardElementState; cvv: CardElementState }
        ) => {
          if (serverError) {
            setServerError(null);
          }
          setClientValidationErrors(input, value);
        }}
        isCouponInputShown={false}
      />
      {serverError && <Typography color="error">{serverError}</Typography>}
      <Button
        testId="update-payment-submit"
        text={<FormattedMessage defaultMessage="Save" />}
        type="submit"
        variant="primary"
        width="full"
        loading={submitting}
      />
      <Button
        linkInfo={{ href: '/my/profile/edit' }}
        onClick={undefined}
        testId=""
        text={<FormattedMessage defaultMessage="Cancel" />}
        variant="flat"
      />
    </form>
  );
};

const UpdatePaymentPage = ({ updateBillingUrl, formData, currencyCode, languageRegionCode }: Props) => {
  const ccTokenizationEnabled = useFeatures().includes('credit_card_tokenization');
  return (
    <CustomProvider>
      <div className={styles.billingContainer}>
        <div className={styles.title}>
          <FormattedMessage defaultMessage="Credit card" />
        </div>
        <div>
          {ccTokenizationEnabled ? (
            <NoSsr>
              <Suspense fallback={null}>
                <RecurlyProvider publicKey={__AT_DATA__.recurlyPublicKey}>
                  <Elements>
                    <TokenizedUpdatePaymentForm updateBillingUrl={updateBillingUrl} formData={formData} />
                  </Elements>
                </RecurlyProvider>
              </Suspense>
            </NoSsr>
          ) : (
            <UpdatePaymentForm
              updateBillingUrl={updateBillingUrl}
              formData={formData}
              currencyCode={currencyCode}
              languageRegionCode={languageRegionCode}
            />
          )}
        </div>
      </div>
    </CustomProvider>
  );
};

export default UpdatePaymentPage;
